import { useEffect, useState } from 'react';
import {
  Alert,
  Backdrop,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import PrimaryButton from '../common/buttons/PrimaryButton';
import { getApiErrorMessage, mapApiValidationErrors } from '../../utils/apiError';
import {
  createStudentClass,
  fetchEduLevelSubjects,
  fetchEducationLevels,
  fetchScheduleClassTypes,
  fetchScheduleGrades,
  fetchScheduleMediums,
  fetchTeacherProfile,
} from '../../services/studentSchedule.service';

const initialForm = {
  educationLevelCode: '',
  gradeId: '',
  subjectId: '',
  mediumId: '',
  classTypeId: '',
  hasTeacher: 'no',
  teacherCode: '',
  teacherId: '',
};

const selectSx = {
  width: '156px',
  minWidth: 0,
  '& .MuiOutlinedInput-root': {
    color: 'white',
    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
    '&:hover fieldset': { borderColor: '#F6911D' },
    '&.Mui-focused fieldset': { borderColor: '#F6911D' },
    '&.Mui-disabled fieldset': { borderColor: '#222222', color: '#222222' },
  },
  '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.6)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#F6911D' },
  '& .MuiFormHelperText-root': { color: 'rgba(255, 255, 255, 0.75)' },
  '& .MuiSvgIcon-root': { color: 'rgba(255, 255, 255, 0.6)' },
};

const textSx = (state = 'default') => ({
  '& .MuiOutlinedInput-root': {
    color: 'white',
    '& fieldset': {
      borderColor:
        state === 'error' ? '#ef4444' : state === 'success' ? '#46d764' : 'rgba(255, 255, 255, 0.2)',
    },
    '&:hover fieldset': {
      borderColor: state === 'error' ? '#ef4444' : state === 'success' ? '#46d764' : '#F6911D',
    },
    '&.Mui-focused fieldset': {
      borderColor: state === 'error' ? '#ef4444' : state === 'success' ? '#46d764' : '#F6911D',
    },
    '&.Mui-disabled fieldset': { borderColor: '#cccccc' },
  },
  '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.6)' },
  '& .MuiInputLabel-root.Mui-focused': { color: state === 'error' ? '#ef4444' : state === 'success' ? '#46d764' : '#F6911D' },
  '& .MuiFormHelperText-root': { color: '#CCCCCC' },
});

const normalizeSubmitData = (payload) => {
  const data = payload?.data || payload || {};

  return {
    educationLevelCode:
      data.education_level_code || data.educationLevelCode || data.education_level?.code || '',
    gradeId: data.grade_id || data.gradeId || '',
    subjectId:
      data.subject_id ||
      data.subjectId ||
      data.grade_subject_id ||
      data.edu_level_subject_id ||
      data.eduLevelSubjectId ||
      '',
    mediumId: data.medium_id || data.mediumId || '',
    classTypeId: data.class_type_id || data.classTypeId || '',
    teacherId: data.teacher_id ?? data.teacherId ?? null,
  };
};

const ScheduleClassModal = ({ open, onClose, studentId, studentName = 'Student', onSuccess }) => {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [educationLevels, setEducationLevels] = useState([]);
  const [grades, setGrades] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [mediums, setMediums] = useState([]);
  const [classTypes, setClassTypes] = useState([]);
  const [teacher, setTeacher] = useState(null);
  const [baseLoading, setBaseLoading] = useState(false);
  const [educationLevelsLoading, setEducationLevelsLoading] = useState(false);
  const [subjectsLoading, setSubjectsLoading] = useState(false);
  const [teacherLookupLoading, setTeacherLookupLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [baseError, setBaseError] = useState('');
  const [educationLevelsError, setEducationLevelsError] = useState('');
  const [subjectsError, setSubjectsError] = useState('');
  const [teacherError, setTeacherError] = useState('');
  const [submitError, setSubmitError] = useState('');

  const resetModal = () => {
    setForm(initialForm);
    setErrors({});
    setEducationLevels([]);
    setGrades([]);
    setSubjects([]);
    setMediums([]);
    setClassTypes([]);
    setTeacher(null);
    setBaseLoading(false);
    setEducationLevelsLoading(false);
    setSubjectsLoading(false);
    setTeacherLookupLoading(false);
    setSubmitting(false);
    setBaseError('');
    setEducationLevelsError('');
    setSubjectsError('');
    setTeacherError('');
    setSubmitError('');
  };

  useEffect(() => {
    if (!open) {
      resetModal();
      return undefined;
    }

    let cancelled = false;

    const loadBaseData = async () => {
      setBaseLoading(true);
      setBaseError('');
      setEducationLevelsLoading(true);
      setEducationLevelsError('');

      const [educationLevelsResult, gradesResult, mediumsResult, classTypesResult] = await Promise.allSettled([
        fetchEducationLevels(),
        fetchScheduleGrades(),
        fetchScheduleMediums(),
        fetchScheduleClassTypes(),
      ]);

      if (cancelled) return;

      if (educationLevelsResult.status === 'fulfilled') {
        setEducationLevels(educationLevelsResult.value);
      } else {
        setEducationLevels([]);
      }

      if (gradesResult.status === 'fulfilled') {
        setGrades(gradesResult.value);
      } else {
        setGrades([]);
      }

      if (mediumsResult.status === 'fulfilled') {
        setMediums(mediumsResult.value);
      }

      if (classTypesResult.status === 'fulfilled') {
        setClassTypes(classTypesResult.value);
      }

      const failures = [];
      if (educationLevelsResult.status === 'rejected') failures.push('education levels');
      if (gradesResult.status === 'rejected') failures.push('grades');
      if (mediumsResult.status === 'rejected') failures.push('mediums');
      if (classTypesResult.status === 'rejected') failures.push('class types');

      if (failures.length > 0) {
        setBaseError(`Unable to load ${failures.join(', ')}. Please try again.`);
      }

      setBaseLoading(false);
      setEducationLevelsLoading(false);
    };

    loadBaseData();

    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const educationLevelCode = form.educationLevelCode;

    if (!educationLevelCode) {
      setSubjects([]);
      setSubjectsError('');
      setSubjectsLoading(false);
      return undefined;
    }

    let cancelled = false;

    const loadSubjects = async () => {
      setSubjectsLoading(true);
      setSubjectsError('');
      setSubjects([]);

      try {
        const data = await fetchEduLevelSubjects(educationLevelCode);
        if (!cancelled) setSubjects(data);
      } catch (error) {
        if (!cancelled) {
          setSubjectsError(getApiErrorMessage(error, 'Unable to load subjects.'));
        }
      } finally {
        if (!cancelled) setSubjectsLoading(false);
      }
    };

    loadSubjects();

    return () => {
      cancelled = true;
    };
  }, [form.educationLevelCode, open]);

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const handleFieldChange = (field) => (event) => {
    const value = event.target.value;

    setForm((current) => {
      const next = { ...current, [field]: value };

      if (field === 'educationLevelCode') {
        next.subjectId = '';
        next.gradeId = '';
        next.teacherId = '';
        next.teacherCode = '';
        setSubjects([]);
        setSubjectsError('');
        setTeacher(null);
        setTeacherError('');
      }

      if (field === 'gradeId') {
        next.teacherId = '';
        next.teacherCode = '';
        setTeacher(null);
        setTeacherError('');
      }

      if (field === 'hasTeacher') {
        next.teacherId = '';
        next.teacherCode = '';
        setTeacher(null);
        setTeacherError('');
        if (value === 'no') {
          setErrors((currentErrors) => {
            const { teacherCode: _teacherCode, ...rest } = currentErrors;
            return rest;
          });
        }
      }

      return next;
    });

    setErrors((currentErrors) => {
      const { [field]: _removed, teacherCode: _teacherCode, teacherId: _teacherId, ...rest } = currentErrors;
      if (field === 'educationLevelCode') {
        const { subjectId: _subjectId, gradeId: _gradeId, ...remaining } = rest;
        return remaining;
      }
      if (field === 'hasTeacher') return rest;
      return rest;
    });
    setSubmitError('');

  };

  const handleTeacherCodeChange = (event) => {
    const value = event.target.value;
    setForm((current) => ({ ...current, teacherCode: value, teacherId: '' }));
    setTeacher(null);
    setTeacherError('');
    setErrors((currentErrors) => {
      const { teacherCode: _removed, teacherId: _teacherId, ...rest } = currentErrors;
      return rest;
    });
    setSubmitError('');
  };

  const handleTeacherSearch = async () => {
    const code = form.teacherCode.trim();
    if (!code) {
      setTeacherError('Teacher Code is required.');
      setErrors((currentErrors) => ({ ...currentErrors, teacherCode: 'Teacher Code is required.' }));
      return;
    }

    setTeacherLookupLoading(true);
    setTeacherError('');
    setSubmitError('');

    try {
      const data = await fetchTeacherProfile(code);
      setTeacher(data);
      setForm((current) => ({ ...current, teacherId: data.id || '' }));
    } catch (error) {
      setTeacher(null);
      setForm((current) => ({ ...current, teacherId: '' }));
      const message = error?.response?.status === 404 ? 'Teacher not found.' : getApiErrorMessage(error, 'Teacher lookup failed.');
      setTeacherError(message);
      setErrors((currentErrors) => ({ ...currentErrors, teacherCode: message }));
    } finally {
      setTeacherLookupLoading(false);
    }
  };

  const validate = () => {
    const nextErrors = {};

    if (!form.educationLevelCode) nextErrors.educationLevelCode = 'Education Level is required.';
    if (!form.gradeId) nextErrors.gradeId = 'Grade is required.';
    if (!form.subjectId) nextErrors.subjectId = 'Subject is required.';
    if (!form.mediumId) nextErrors.mediumId = 'Medium is required.';
    if (!form.classTypeId) nextErrors.classTypeId = 'Class Type is required.';
    if (!form.hasTeacher) nextErrors.hasTeacher = 'Please select Yes or No.';

    if (form.hasTeacher === 'yes') {
      if (!form.teacherCode.trim()) {
        nextErrors.teacherCode = 'Teacher Code is required.';
      }
      if (!form.teacherId) {
        nextErrors.teacherCode = nextErrors.teacherCode || teacherError || 'Please search and select a valid teacher.';
      }
    }

    return nextErrors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const nextErrors = validate();
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0 || !studentId) {
      if (!studentId) setSubmitError('A student must be selected before scheduling a class.');
      return;
    }

    setSubmitting(true);
    setSubmitError('');

    try {
      const payload = {
        grade_id: form.gradeId,
        education_level_subject_id: form.subjectId,
        medium_id: form.mediumId,
        class_type_id: form.classTypeId,
        teacher_profile_id: form.hasTeacher === 'yes' ? form.teacherId : null,
      };

      const response = await createStudentClass(studentId, payload);
      const normalized = normalizeSubmitData(response);
      handleClose();
      await onSuccess?.(normalized);
    } catch (error) {
      const mappedErrors = mapApiValidationErrors(error.response?.data);
      if (Object.keys(mappedErrors).length > 0) {
        setErrors((current) => ({ ...current, ...mappedErrors }));
      } else {
        setSubmitError(getApiErrorMessage(error, 'Unable to schedule class. Please try again.'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const teacherInputState = teacherError ? 'error' : teacher ? 'success' : 'default';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(8px)',
            backgroundColor: 'rgba(0,0,0,0.7)',
          },
        },
        paper: {
          sx: {
            background: 'linear-gradient(158deg, rgba(17,24,39,1) 0%, rgba(15,15,18,1) 100%)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '12px',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
          },
        },
      }}
    >
      <IconButton
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: 'white',
          zIndex: 1,
          '&:hover': {
            background: 'rgba(229,64,4,0.1)',
            color: '#F6911D',
            transition: 'all 0.3s ease',
          },
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogTitle sx={{ color: 'white', pr: 6 }}>
        <div className="text-2xl font-semibold">Schedule Class for {studentName}</div>
        <div className="mt-1 text-sm text-white/60">
          Create a new class and attach it to this student.
        </div>
      </DialogTitle>

      <DialogContent
        sx={{ p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1 }}
      >
        <form onSubmit={handleSubmit} className="space-y-5 p-6 pt-2">
          {baseLoading ? (
            <div className="rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-sm text-white/70">
              Loading class options...
            </div>
          ) : null}

          {baseError ? (
            <Alert
              severity="error"
              sx={{ backgroundColor: 'rgba(211,47,47,0.14)', color: 'white' }}
            >
              {baseError}
            </Alert>
          ) : null}

          {submitError ? (
            <Alert
              severity="error"
              sx={{ backgroundColor: 'rgba(211,47,47,0.14)', color: 'white' }}
            >
              {submitError}
            </Alert>
          ) : null}

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Education Level"
                value={form.educationLevelCode ?? ''}
                onChange={handleFieldChange('educationLevelCode')}
                disabled={baseLoading || educationLevelsLoading || educationLevels.length === 0}
                error={Boolean(errors.educationLevelCode) || Boolean(educationLevelsError)}
                helperText={
                  errors.educationLevelCode ||
                  educationLevelsError ||
                  (!baseLoading && !educationLevelsLoading && educationLevels.length === 0
                    ? 'No education levels available.'
                    : '')
                }
                sx={selectSx}
              >
                <MenuItem value="" disabled>
                  {educationLevelsLoading ? 'Loading education levels...' : 'Select an education level'}
                </MenuItem>
                {educationLevels.map((educationLevel, index) => (
                  <MenuItem key={`${educationLevel.id}-${index}`} value={educationLevel.code ?? ''}>
                    {educationLevel.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Subject"
                value={form.subjectId ?? ''}
                onChange={handleFieldChange('subjectId')}
                disabled={!form.educationLevelCode || subjectsLoading || subjects.length === 0 || Boolean(subjectsError)}
                error={Boolean(errors.subjectId) || Boolean(subjectsError)}
                helperText={
                  errors.subjectId ||
                  subjectsError ||
                  (!form.educationLevelCode
                    ? 'Select an education level first.'
                    : !subjectsLoading && subjects.length === 0
                      ? 'No subjects available for this education level.'
                      : '')
                }
                sx={selectSx}
              >
                <MenuItem value="" disabled>
                  {subjectsLoading ? 'Loading subjects...' : 'Select a subject'}
                </MenuItem>
                {subjects.map((subject, index) => (
                  <MenuItem key={`${subject.id}-${index}`} value={subject.id ?? ''}>
                    {subject.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Grade"
                value={form.gradeId ?? ''}
                onChange={handleFieldChange('gradeId')}
                disabled={baseLoading || grades.length === 0}
                error={Boolean(errors.gradeId)}
                helperText={
                  errors.gradeId || (!baseLoading && grades.length === 0 ? 'No grades available.' : '')
                }
                sx={selectSx}
              >
                <MenuItem value="" disabled>
                  Select a grade
                </MenuItem>
                {grades.map((grade, index) => (
                  <MenuItem key={`${grade.id}-${index}`} value={grade.id ?? ''}>
                    {grade.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Medium"
                value={form.mediumId ?? ''}
                onChange={handleFieldChange('mediumId')}
                disabled={baseLoading || mediums.length === 0}
                error={Boolean(errors.mediumId)}
                helperText={
                  errors.mediumId ||
                  (!baseLoading && mediums.length === 0 ? 'No mediums available.' : '')
                }
                sx={selectSx}
              >
                <MenuItem value="" disabled>
                  Select a medium
                </MenuItem>
                {mediums.map((medium, index) => (
                  <MenuItem key={`${medium.id}-${index}`} value={medium.id ?? ''}>
                    {medium.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Class Type"
                value={form.classTypeId ?? ''}
                onChange={handleFieldChange('classTypeId')}
                disabled={baseLoading || classTypes.length === 0}
                error={Boolean(errors.classTypeId)}
                helperText={
                  errors.classTypeId ||
                  (!baseLoading && classTypes.length === 0 ? 'No class types available.' : '')
                }
                sx={selectSx}
              >
                <MenuItem value="" disabled>
                  Select a class type
                </MenuItem>
                {classTypes.map((classType, index) => (
                  <MenuItem key={`${classType.id}-${index}`} value={classType.id ?? ''}>
                    {classType.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <FormControl error={Boolean(errors.hasTeacher)}>
                <div className="mb-2 text-sm text-white/70">Already Have a Teacher?</div>
                <RadioGroup row value={form.hasTeacher} onChange={handleFieldChange('hasTeacher')}>
                  <FormControlLabel
                    value="yes"
                    control={
                      <Radio sx={{ color: '#CCCCCC', '&.Mui-checked': { color: '#CCCCCC' } }} />
                    }
                    label="Yes"
                    sx={{ color: '#CCCCCC', '& .MuiFormControlLabel-label': { color: '#CCCCCC' } }}
                  />
                  <FormControlLabel
                    value="no"
                    control={
                      <Radio sx={{ color: '#CCCCCC', '&.Mui-checked': { color: '#CCCCCC' } }} />
                    }
                    label="No"
                    sx={{ color: '#CCCCCC', '& .MuiFormControlLabel-label': { color: '#CCCCCC' } }}
                  />
                </RadioGroup>
                <FormHelperText>{errors.hasTeacher}</FormHelperText>
              </FormControl>
            </Grid>

            {form.hasTeacher === 'yes' ? (
              <Grid item xs={12}>
                <div className="grid gap-3 md:grid-cols-[1fr_auto] md:items-start">
                  <TextField
                    fullWidth
                    label="Teacher Code"
                    value={form.teacherCode}
                    onChange={handleTeacherCodeChange}
                    error={Boolean(errors.teacherCode) || Boolean(teacherError)}
                    helperText={
                      errors.teacherCode ||
                      teacherError ||
                      'Search to validate the teacher before submitting.'
                    }
                    sx={textSx(teacherInputState)}
                    InputProps={{
                      endAdornment: teacherLookupLoading ? (
                        <InputAdornment position="end">
                          <CircularProgress size={18} sx={{ color: '#F6911D' }} />
                        </InputAdornment>
                      ) : null,
                    }}
                  />

                  <PrimaryButton
                    type="button"
                    onClick={handleTeacherSearch}
                    disabled={teacherLookupLoading || !form.teacherCode.trim()}
                    startIcon={
                      teacherLookupLoading ? (
                        <CircularProgress size={16} sx={{ color: 'white' }} />
                      ) : (
                        <SearchIcon />
                      )
                    }
                    sx={{ minHeight: 56, minWidth: 140 }}
                  >
                    Search
                  </PrimaryButton>
                </div>

                {teacher ? (
                  <Alert
                    icon={<CheckCircleOutlineIcon fontSize="inherit" />}
                    severity="success"
                    sx={{ mt: 2, backgroundColor: 'rgba(70,215,100,0.12)', color: '#c9f8d2' }}
                  >
                    <div className="font-medium">{teacher.fullName}</div>
                    <div className="text-sm">Teacher Code: {teacher.code}</div>
                  </Alert>
                ) : null}
              </Grid>
            ) : null}
          </Grid>

          <div className="flex flex-col gap-3 border-t border-white/10 pt-5 sm:flex-row sm:items-center sm:justify-end">
            <PrimaryButton
              type="button"
              onClick={handleClose}
              disabled={submitting || teacherLookupLoading}
              sx={{ background: 'rgba(255,255,255,0.08)' }}
            >
              Cancel
            </PrimaryButton>
            <PrimaryButton
              type="submit"
              disabled={submitting || baseLoading || teacherLookupLoading || !studentId}
            >
              {submitting ? (
                <CircularProgress size={24} sx={{ color: 'white' }} />
              ) : (
                'Schedule Class'
              )}
            </PrimaryButton>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ScheduleClassModal;
